import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, Info, Loader2, X } from 'lucide-react';

type Tom = 'danger' | 'warn' | 'info';

interface Props {
  open: boolean;
  titulo: string;
  mensagem?: ReactNode;
  textareaLabel?: string;
  textareaPlaceholder?: string;
  textareaMinChars?: number;
  textareaMaxChars?: number;
  textoPreenchido?: string;
  exigeDigitacao?: string;
  tom?: Tom;
  confirmLabel?: string;
  cancelLabel?: string;
  onCancel: () => void;
  onConfirm: (
    texto: string,
  ) => Promise<{ sucesso: boolean; mensagem?: string } | void>;
}

const CLS: Record<Tom, {
  border: string; icon: string; boxBg: string; boxBorder: string; boxText: string; btn: string;
}> = {
  danger: {
    border: 'border-rose-300 dark:border-rose-500/30', icon: 'text-rose-600 dark:text-rose-400',
    boxBg: 'bg-rose-50 dark:bg-rose-500/10', boxBorder: 'border-rose-200 dark:border-rose-500/30', boxText: 'text-rose-800 dark:text-rose-300',
    btn: 'btn-danger',
  },
  warn: {
    border: 'border-amber-300 dark:border-amber-500/30', icon: 'text-amber-600 dark:text-amber-400',
    boxBg: 'bg-amber-50 dark:bg-amber-500/10', boxBorder: 'border-amber-200 dark:border-amber-500/30', boxText: 'text-amber-800 dark:text-amber-200',
    btn: 'btn-amber',
  },
  info: {
    border: 'border-sky-300 dark:border-sky-500/30', icon: 'text-sky-600 dark:text-sky-400',
    boxBg: 'bg-sky-50 dark:bg-sky-500/10', boxBorder: 'border-sky-200 dark:border-sky-500/30', boxText: 'text-sky-800 dark:text-sky-300',
    btn: 'btn-sky',
  },
};

export default function ConfirmacaoModal({
  open,
  titulo,
  mensagem,
  textareaLabel = 'Justificativa',
  textareaPlaceholder,
  textareaMinChars = 0,
  textareaMaxChars = 1000,
  textoPreenchido = '',
  exigeDigitacao,
  tom = 'warn',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Voltar',
  onCancel,
  onConfirm,
}: Props) {
  const [texto, setTexto] = useState(textoPreenchido);
  const [confirmWord, setConfirmWord] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<
    { tipo: 'ok' | 'err'; msg: string } | null
  >(null);

  useEffect(() => {
    if (open) {
      setTexto(textoPreenchido);
      setConfirmWord('');
      setFeedback(null);
      setLoading(false);
    }
  }, [open, textoPreenchido]);

  if (!open) return null;

  const cls = CLS[tom];
  const tLen = texto.trim().length;
  const textoValido = tLen >= textareaMinChars && tLen <= textareaMaxChars;
  const digitacaoOK =
    !exigeDigitacao ||
    confirmWord.trim().toUpperCase() === exigeDigitacao.toUpperCase();
  const formOK = textoValido && digitacaoOK && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formOK) return;
    setLoading(true);
    setFeedback(null);
    try {
      const res = await onConfirm(texto.trim());
      if (res && typeof res === 'object') {
        if (res.sucesso === false) {
          setFeedback({ tipo: 'err', msg: res.mensagem ?? 'Falha ao confirmar.' });
          setLoading(false);
          return;
        }
        if (res.mensagem) {
          setFeedback({ tipo: 'ok', msg: res.mensagem });
          setTimeout(() => onCancel(), 1500);
          return;
        }
      }
      onCancel();
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : 'Erro desconhecido.';
      setFeedback({ tipo: 'err', msg });
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm dark:bg-black/70"
      onClick={loading ? undefined : onCancel}
    >
      <div
        className={`flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border bg-white shadow-2xl dark:bg-ink-900 ${cls.border}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <div className="flex items-center gap-2">
            {tom === 'info' ? (
              <Info className={`h-5 w-5 ${cls.icon}`} />
            ) : (
              <AlertTriangle className={`h-5 w-5 ${cls.icon}`} />
            )}
            <h3 className="font-semibold text-slate-950 dark:text-white">{titulo}</h3>
          </div>
          <button
            type="button"
            className="btn-ghost !h-9 !w-9 !px-2 !py-1"
            onClick={onCancel}
            disabled={loading}
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
        {mensagem && (
          <div
            className={`rounded-lg ${cls.boxBg} border ${cls.boxBorder} ${cls.boxText} px-3 py-2 text-sm mb-4`}
          >
            {mensagem}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="field-label">
              {textareaLabel}
              {textareaMinChars > 0 && <span className={cls.icon}> *</span>}
            </label>
            <textarea
              className="field-input"
              rows={3}
              minLength={textareaMinChars || undefined}
              maxLength={textareaMaxChars}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder={
                textareaPlaceholder ??
                (textareaMinChars > 0
                  ? `Mínimo ${textareaMinChars} caracteres.`
                  : undefined)
              }
              disabled={loading}
              required={textareaMinChars > 0}
              autoFocus
            />
            <p
              className={`text-xs mt-1 ${
                tLen < textareaMinChars ? cls.icon : 'text-neutral-500'
              }`}
            >
              {tLen}/{textareaMaxChars}
              {tLen < textareaMinChars &&
                ` (faltam ${textareaMinChars - tLen} para o mínimo)`}
            </p>
          </div>

          {exigeDigitacao && (
            <div>
              <label className="field-label">
                Digite{' '}
                <span className={`mono font-bold ${cls.icon}`}>
                  {exigeDigitacao}
                </span>{' '}
                para confirmar
              </label>
              <input
                type="text"
                className="field-input mono uppercase"
                value={confirmWord}
                onChange={(e) => setConfirmWord(e.target.value)}
                placeholder={exigeDigitacao}
                disabled={loading}
                autoComplete="off"
              />
            </div>
          )}

          {feedback && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                feedback.tipo === 'ok'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
                  : 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300'
              }`}
            >
              {feedback.msg}
            </div>
          )}

          <div className="-mx-5 -mb-5 flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-white/10 dark:bg-slate-950/70">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="btn-secondary"
            >
              {cancelLabel}
            </button>
            <button type="submit" disabled={!formOK} className={cls.btn}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? 'Processando...' : confirmLabel}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}
